import {Component, Element, Event, EventEmitter, h, Method, Prop} from '@stencil/core';

type ToggleType = {
    open: boolean;
    emitEvent: boolean;
};

/**
 * @slot collapse-summary - The main text that is still visible when component is closed. Placed before the content
 * @slot collapse-marker - Visual or textual marker used to mark the open/close state of the component. It can be an icon (arrow) or pure text. Placed before the content
 * @slot collapse-detail - Hidden content shown when clicking on summary or marker.
 * @part collapse-detail - Wrapper for collapse-detail slot, inside the shadow DOM.
 * @part collapse-summary - Wrapper for collapse-summary slot, inside the shadow DOM.
 */
@Component({
    tag: 'joy-collapse-item',
    styleUrl: 'collapse-item.scss',
    shadow: true,
})
export class JoyCollapseItem {
    private summaryEl!: HTMLElement;
    private collapseItemWrapper!: HTMLElement;

    @Element() host!: HTMLJoyCollapseItemElement;

    /** Default to false. Open or close the collapse item. */
    @Prop({reflect: true, mutable: true}) open = false;
    /** Display an icon before the summary. */
    @Prop() summaryIcon?: string;

    /** Every time you click to toggle the collapse-item, the event is fired */
    @Event({eventName: 'joy-collapse-item-click'}) joyCollapseItemClick!: EventEmitter<void>;
    /** This event is fired only when you open the collapse-item */
    @Event({eventName: 'joy-collapse-item-open'}) joyCollapseItemOpen!: EventEmitter<void>;
    /** This event is fired only when you close the collapse-item */
    @Event({eventName: 'joy-collapse-item-close'}) joyCollapseItemClose!: EventEmitter<void>;

    /**
     *
     * @param {string} params - If you're facing issue with an item opened by default and not having the right calculated height, you can still use this method
     * in order to trigger the toggle during the right framework lifecycle hook.
     * @param {boolean} params.open - If you want to open the collapse-item. Default to true.
     * @param {boolean} params.emitEvent - Emits a custom event based on whether we open or close the item. Default to false.
     */
    @Method()
    async toggle(
        params: ToggleType = {
            open: true,
            emitEvent: false,
        },
    ) {
        /** Updating open value triggers the @Watch method */
        this.open = params.open;

        if (params.emitEvent) {
            this.open ? this.joyCollapseItemOpen.emit() : this.joyCollapseItemClose.emit();
        }
    }
    /**
     * @internal
     * @deprecated
     */
    @Method()
    async resetCollapseItemState() {
        this.collapseItemWrapper.style.height = '0';
    }

    private onToggle = () => {
        this.open = !this.open;

        this.emitEvents();
    };

    private emitEvents() {
        this.joyCollapseItemClick.emit();
        this.open ? this.joyCollapseItemOpen.emit() : this.joyCollapseItemClose.emit();
    }

    render() {
        return (
            <div
                class={{
                    'joy-collapse-item': true,
                    'joy-collapse-item--open': this.open,
                }}
            >
                <div
                    class={{
                        'joy-collapse-item__summary': true,
                    }}
                    onClick={this.onToggle}
                    ref={(el) => (this.summaryEl = el as HTMLElement)}
                    part="collapse-summary"
                >
                    <div class="joy-collapse-item__summary___content">
                        {this.summaryIcon && <joy-icon color="teal" name={this.summaryIcon}></joy-icon>}
                        <slot name="collapse-summary" />
                    </div>
                    <slot name="collapse-marker">
                        <joy-icon class="joy-collapse-item__summary___marker" name="chevron-down"></joy-icon>
                    </slot>
                </div>

                <div
                    ref={(el) => (this.collapseItemWrapper = el as HTMLElement)}
                    class={{
                        'joy-collapse-item__detail': true,
                        'joy-collapse-item__detail--open': this.open,
                    }}
                    part="collapse-detail"
                >
                    <slot name="collapse-detail" />
                </div>
            </div>
        );
    }
}
